import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors } from '@/design-system/theme/colors';
import { CreditStatus } from '@/types';
import { useTranslation } from '@/hooks/useTranslation';

const DISMISSED_KEY = '@credit_warning_dismissed_at';
const SNOOZE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours

interface LowCreditWarningBannerProps {
  creditStatus: CreditStatus | null;
  onBuyCredits?: () => void;
}

/**
 * LowCreditWarningBanner - Dismissible banner shown when usage >= 80%
 *
 * Features:
 * - Shows when low_credit_warning is true
 * - Dismissible via AsyncStorage (snoozed for 6 hours, then reappears)
 * - CTA to buy more credits
 */
export function LowCreditWarningBanner({
  creditStatus,
  onBuyCredits,
}: LowCreditWarningBannerProps) {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(true); // Default to hidden until we check

  // Check if banner was snoozed recently
  useEffect(() => {
    const checkDismissed = async () => {
      try {
        const dismissedAt = await AsyncStorage.getItem(DISMISSED_KEY);
        if (dismissedAt) {
          const elapsed = Date.now() - Number(dismissedAt);
          if (elapsed < SNOOZE_DURATION_MS) {
            // Still within snooze window
            setIsDismissed(true);
            return;
          }
        }
        setIsDismissed(false);
      } catch {
        setIsDismissed(false);
      }
    };
    checkDismissed();
  }, [creditStatus?.low_credit_warning]);

  const handleDismiss = async () => {
    setIsDismissed(true);
    try {
      await AsyncStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // Ignore storage errors
    }
  };

  // Don't show if:
  // - No credit status
  // - No low credit warning
  // - Usage is hidden (B2B2C students)
  // - Already dismissed
  if (
    !creditStatus ||
    !creditStatus.low_credit_warning ||
    creditStatus.usage_hidden ||
    creditStatus.credit_exempt ||
    isDismissed
  ) {
    return null;
  }

  const remaining = creditStatus.total_available ?? 0;
  const subscriptionTotal = creditStatus.subscription_credits?.total ?? 0;

  return (
    <View className="mx-4 mb-3 rounded-lg bg-amber-50 border border-amber-200 overflow-hidden">
      <View className="flex-row items-center p-3 gap-3">
        {/* Warning Icon */}
        <View className="w-10 h-10 rounded-full bg-amber-100 items-center justify-center">
          <MaterialIcons name="warning" size={24} color={colors.warning} />
        </View>

        {/* Content */}
        <View className="flex-1">
          <Text className="text-sm font-semibold text-amber-800">
            {t('dashboard.runningLowOnCredits')}
          </Text>
          <Text className="text-xs text-amber-700 mt-0.5">
            {subscriptionTotal > 0
              ? t('dashboard.creditsRemaining')
                  .replace('{{remaining}}', String(remaining))
                  .replace('{{total}}', String(subscriptionTotal))
              : t('dashboard.creditsRemainingNoTotal')
                  .replace('{{remaining}}', String(remaining))
            }
          </Text>
        </View>

        {/* Dismiss Button */}
        <TouchableOpacity
          onPress={handleDismiss}
          className="w-8 h-8 items-center justify-center"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <MaterialIcons name="close" size={20} color={colors.gray[500]} />
        </TouchableOpacity>
      </View>

      {/* Buy Credits CTA */}
      {onBuyCredits && (
        <TouchableOpacity
          onPress={onBuyCredits}
          className="flex-row items-center justify-center gap-1.5 py-2.5 bg-amber-100 border-t border-amber-200"
          activeOpacity={0.7}
        >
          <MaterialIcons name="add-circle-outline" size={18} color={colors.warning} />
          <Text className="text-sm font-semibold text-amber-800">
            {t('dashboard.buyMoreCredits')}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

export default LowCreditWarningBanner;
